import type { SchemaField, SchemaOption, SchemaQuestion } from "../../types";

/**
 * Renders a saved value as the plain-language line the review reads (US-E3-09: "long answers are
 * shown in full, not truncated"). `null` means "unanswered" — the caller skips the row entirely
 * rather than showing an empty line, because "what's missing" is already `ReviewMissingItems`'s
 * job; a blank row here would say the same thing twice, once vaguely.
 *
 * Deliberately duplicates `DossierField`'s `keyOf`/`valueForKey` value↔label lookup rather than
 * importing it — that pair is scoped to an editing control's onChange/onCommit wiring, and reusing
 * it here would mean exporting editing internals for a read-only concern that has no `onChange` to
 * share.
 */
export function formatFieldValue(
  field: Pick<SchemaField | SchemaQuestion, "input_type" | "options">,
  value: unknown
): string | null {
  if (value === null || value === undefined) return null;

  const labelFor = (raw: unknown): string | null => {
    const option = field.options.find(
      (candidate: SchemaOption) => String(candidate.value) === String(raw)
    );
    return option?.label ?? null;
  };

  switch (field.input_type) {
    case "MULTI_SELECT": {
      if (!Array.isArray(value) || value.length === 0) return null;
      const labels = value.map((entry) => labelFor(entry) ?? String(entry));
      return labels.join(", ");
    }
    case "DROPDOWN":
    case "RADIO":
      if (value === "") return null;
      return labelFor(value) ?? String(value);
    case "CHECKBOX":
      // A boolean consent/toggle field, not a catalogue choice — `residency_confirmed` and
      // `named_members_consent_confirmed` both go through here with no `options` at all.
      return typeof value === "boolean" ? null : String(value);
    case "NUMBER":
      return typeof value === "number" ? String(value) : null;
    default: {
      const text = typeof value === "string" ? value.trim() : String(value);
      return text === "" ? null : text;
    }
  }
}

/**
 * `CHECKBOX` is the one input type `formatFieldValue` deliberately renders as `null` above — a
 * yes/no toggle reads better as a short localized word than as `"true"`. Split out so a caller
 * that HAS a translation function on hand (every actual caller does) can supply it, without this
 * module — otherwise translation-free — taking a hook dependency it does not need.
 */
export function formatBooleanValue(
  value: unknown,
  labels: { yes: string; no: string }
): string | null {
  if (typeof value !== "boolean") return null;
  return value ? labels.yes : labels.no;
}
